/**
 * GH-268 — TourStep popover.
 *
 * Stateless presentational popover. Reads the active step from `useTour()`
 * and renders:
 *   - Route hint (e.g. `/packages/:templateId`) in mono above the title
 *   - Title + body
 *   - "Step N of M" counter and an animated progress bar
 *   - Skip / Back / Next (Finish on the last step)
 *
 * Motion:
 *   - The popover fades + scales-in on appear (200ms) and slides-up on exit
 *     via a CSS keyframe gated on `prefers-reduced-motion: no-preference`.
 *   - The progress bar's filled width transitions smoothly between steps.
 *
 * a11y:
 *   - role="dialog" + aria-modal="false" (non-blocking coach mark)
 *   - aria-labelledby points at the title element
 *   - The progress bar exposes role="progressbar" with valuenow/min/max
 *   - ESC key triggers `skip()` (handled in `<TourOverlay/>` to keep this
 *     component pure and easy to test)
 */

import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { useTour } from './use-tour'

export interface TourStepProps {
  /**
   * Optional fallback hint shown when the active step's anchor cannot be
   * resolved on this page. Rendered as a small italic note above the body
   * text. Pass `null` to hide.
   */
  fallbackMessage?: string | null
}

export function TourStep({ fallbackMessage = null }: TourStepProps = {}) {
  const { t } = useTranslation()
  const { currentStep, currentStepIndex, totalSteps, isLastStep, next, prev, skip } = useTour()
  const titleId = useId()

  if (!currentStep || currentStepIndex === null) return null

  const titleKey = `${currentStep.i18nKey}.title`
  const bodyKey = `${currentStep.i18nKey}.body`
  const stepNumber = currentStepIndex + 1
  const progressPct = Math.round((stepNumber / totalSteps) * 100)
  const progressLabel = t('tour.progress.label', {
    current: stepNumber,
    total: totalSteps,
    defaultValue: `Step ${stepNumber} of ${totalSteps}`,
  })

  return (
    <>
      <style>{POPOVER_CSS}</style>
      <div
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        // Re-keying via the wrapper isn't needed — the popover is unmounted
        // and re-mounted by parent on step change, replaying the entrance.
        className="tour-popover-enter rounded-2xl border border-neutral-200/80 bg-white p-4 shadow-[0_18px_42px_-12px_rgba(15,23,42,0.35)] ring-1 ring-black/5 dark:border-white/10 dark:bg-surface-1 dark:ring-white/10"
        style={{ minWidth: 300, maxWidth: 380 }}
      >
        {/* Route hint */}
        <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
          {currentStep.route}
        </div>

        <div className="mb-2 flex items-start justify-between gap-3">
          <h2
            id={titleId}
            className="text-[17px] font-semibold leading-tight text-neutral-900 dark:text-neutral-100"
          >
            {t(titleKey)}
          </h2>
          <span
            className="shrink-0 whitespace-nowrap rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-medium text-neutral-600 dark:bg-surface-2 dark:text-neutral-400"
            aria-hidden
          >
            {stepNumber} / {totalSteps}
          </span>
        </div>

        <p className="mb-3 text-[13px] leading-relaxed text-neutral-700 dark:text-neutral-300">
          {t(bodyKey)}
        </p>

        {fallbackMessage && (
          <p
            role="note"
            className="mb-3 rounded-md border border-amber-200/70 bg-amber-50 px-2.5 py-1.5 text-[12px] italic leading-relaxed text-amber-800 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-200"
          >
            {fallbackMessage}
          </p>
        )}

        {/* Progress bar */}
        <div
          className="mb-4 h-1 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-surface-2"
          role="progressbar"
          aria-valuenow={stepNumber}
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          aria-label={progressLabel}
        >
          <div
            className="tour-popover-progress h-full rounded-full bg-brand-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={skip}
            className="rounded-md px-2 py-1 text-xs text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-surface-2 dark:hover:text-neutral-200"
          >
            {t('tour.actions.skip')}
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={prev}
              disabled={currentStepIndex === 0}
              className="rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-neutral-200 dark:hover:bg-surface-2"
            >
              {t('tour.actions.back')}
            </button>
            <button
              type="button"
              onClick={next}
              className="tour-popover-next rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all hover:bg-brand-600 hover:shadow"
            >
              {isLastStep ? t('tour.actions.finish') : t('tour.actions.next')}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

const POPOVER_CSS = `
.tour-popover-progress {
  transition: width 360ms cubic-bezier(0.16, 1, 0.3, 1);
}
@media (prefers-reduced-motion: no-preference) {
  .tour-popover-enter {
    animation: tour-popover-in 200ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  .tour-popover-next:hover {
    background-image: linear-gradient(135deg, var(--color-brand-500, #3b82f6), var(--color-brand-600, #2563eb));
  }
}
@keyframes tour-popover-in {
  from { opacity: 0; transform: translateY(6px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
`
